import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { StreamingAudienceStore } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../../common/types';
import { changePctOf, formatMonth } from '../../streamingFormat';
import { audienceMessages as messages } from '../../audienceMessages';
import { StreamingBlock } from '../../parts/StreamingBlock';
import { RankedRow } from '../../parts/RankedRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AudienceStoresBlockProps {
  stores: readonly StreamingAudienceStore[];
  month: string;
}

/**
 * Listeners by store (design streaming-sections a1, owner decision O8): only for a source that
 * splits by store (TuneCore byStore). The change is against the month before.
 */
const AudienceStoresBlockComponent: React.FC<AudienceStoresBlockProps> = ({ stores, month }) => {
  const intl = useIntl();
  const rows = useMemo(() => {
    const sorted = [...stores].sort((left, right) => right.listeners - left.listeners);
    const total = sorted.reduce((sum, row) => sum + row.listeners, 0);
    return sorted.map((row, index) => ({
      ...row,
      rank: index + 1,
      share: total > 0 ? row.listeners / total : null,
      changePct: changePctOf(row.listeners, row.previousListeners),
    }));
  }, [stores]);

  return (
    <StreamingBlock
      title={intl.formatMessage(messages.stores)}
      meta={formatMonth(intl, month)}
      testId="streaming-audience-stores"
    >
      <ol className={shared.rows}>
        {rows.map((row) => (
          <RankedRow
            key={row.store}
            rank={row.rank}
            name={row.store}
            share={row.share}
            value={row.listeners}
            changePct={row.changePct}
          />
        ))}
      </ol>
      <p className={shared.footnote}>{intl.formatMessage(messages.storesChangeNote)}</p>
    </StreamingBlock>
  );
};

export const AudienceStoresBlock = TypedMemo(AudienceStoresBlockComponent);
